import { useTranslation } from 'react-i18next';
import { ReactSVG } from 'react-svg';
import { styled, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { IconBolt, IconVariable } from '@tabler/icons-react';

import { Badge, Typography } from '@zendesk/forethought-elements';
import eventTriggerIcon from 'src/assets/images/event-trigger-icon.svg';
import buildingBlockIcon from 'src/pages/workflow-builder-edit/assets/icons/building-block.svg';
import { RestorationRequirements } from 'src/types/workflowBuilderAPITypes';

const CircleIcon = styled('div')<{ bgcolor: string }>`
  background-color: ${props => props.bgcolor};
  border-radius: 16px;
  color: white;
  height: 32px;
  width: 32px;
  padding: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ResourceCard = styled('div')<{ cannotRestore?: boolean }>`
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  padding: 10px 16px;
  background-color: ${props =>
    props.cannotRestore
      ? props.theme.palette.colors.red[100]
      : props.theme.palette.colors.white};
  border: 1px solid
    ${props =>
      props.cannotRestore
        ? props.theme.palette.colors.red[500]
        : props.theme.palette.colors.grey[200]};
`;

const StyledImg = styled('img')`
  height: 20px;
  width: 20px;
`;

const getActionTypeLabel = (actionType: string | null): string => {
  switch (actionType) {
    case 'api_call':
      return 'API';
    case 'mcp':
      return 'MCP';
    case 'browser_agent':
      return 'Browser Agent';
    default:
      return actionType || 'Action';
  }
};

interface RestorationContentProps {
  /** When true, shows error state for items that cannot be restored */
  cannotRestore?: boolean;
  /** Optional introductory text to show before the resource list */
  introText?: string;
  requirements: RestorationRequirements;
}

export const RestorationContent = ({
  cannotRestore,
  introText,
  requirements,
}: RestorationContentProps) => {
  const { t } = useTranslation('workflow-builder-edit');
  const theme = useTheme();
  const {
    deleted_actions,
    deleted_child_workflows,
    deleted_cvs,
    deleted_event_configs,
    modified_actions,
  } = requirements;

  const totalResourceCount =
    deleted_actions.length +
    modified_actions.length +
    deleted_cvs.length +
    deleted_child_workflows.length +
    deleted_event_configs.length;

  const unrestorableCount =
    deleted_actions.filter(a => a.can_restore === false).length +
    deleted_child_workflows.filter(w => w.can_restore === false).length +
    modified_actions.filter(a => a.can_restore === false).length;

  const getDescriptionText = () => {
    if (cannotRestore) {
      return t('restorationContent.cannotRestoreDescription', {
        count: unrestorableCount,
      });
    }

    return `Here ${
      totalResourceCount === 1 ? 'is the' : `are the ${totalResourceCount}`
    } ${
      totalResourceCount === 1 ? 'resource' : 'resources'
    } that will be created, as ${
      totalResourceCount === 1 ? 'it is' : 'they are'
    } not currently available.`;
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        maxWidth: '450px',
      }}
    >
      {introText && !cannotRestore && (
        <Typography variant='font14'>{introText}</Typography>
      )}
      <Typography variant='font14'>{getDescriptionText()}</Typography>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        {deleted_actions.map(action => (
          <ResourceCard
            cannotRestore={cannotRestore && action.can_restore === false}
            key={action.action_id}
          >
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                gap: 1,
              }}
            >
              <CircleIcon bgcolor={theme.palette.colors.blue[500]}>
                <IconBolt size={20} />
              </CircleIcon>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                }}
              >
                <Typography variant='font14Bold'>
                  {action.action_name}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.5,
                  }}
                >
                  <Badge
                    label={getActionTypeLabel(action.action_type)}
                    variant='macro'
                  />
                  {cannotRestore && action.can_restore === false && (
                    <Badge
                      label={t('restorationContent.cannotRestore')}
                      variant='error'
                    />
                  )}
                </Box>
              </Box>
            </Box>
          </ResourceCard>
        ))}

        {modified_actions.map(action => (
          <ResourceCard
            cannotRestore={cannotRestore && action.can_restore === false}
            key={action.action_id}
          >
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                gap: 1,
              }}
            >
              <CircleIcon bgcolor={theme.palette.colors.blue[500]}>
                <IconBolt size={20} />
              </CircleIcon>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                }}
              >
                <Typography variant='font14Bold'>
                  {action.action_name}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.5,
                  }}
                >
                  <Badge
                    label={getActionTypeLabel(action.action_type)}
                    variant='macro'
                  />
                  <Badge
                    label={`Restoring v${action.snapshot_version} (current: v${action.current_version})`}
                    variant='macro'
                  />
                  {cannotRestore && action.can_restore === false && (
                    <Badge
                      label={t('restorationContent.cannotRestore')}
                      variant='error'
                    />
                  )}
                </Box>
              </Box>
            </Box>
          </ResourceCard>
        ))}

        {deleted_cvs.map(cv => (
          <ResourceCard key={cv.context_variable_id}>
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                gap: 1,
              }}
            >
              <CircleIcon bgcolor={theme.palette.colors.purple[500]}>
                <IconVariable size={20} />
              </CircleIcon>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                }}
              >
                <Typography variant='font14Bold'>{cv.display_name}</Typography>
                <Badge
                  label={t('restorationContent.contextVariable')}
                  variant='macro'
                />
              </Box>
            </Box>
          </ResourceCard>
        ))}

        {deleted_child_workflows.map(workflow => (
          <ResourceCard
            cannotRestore={cannotRestore && workflow.can_restore === false}
            key={workflow.intent_definition_id}
          >
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                gap: 1,
              }}
            >
              <CircleIcon bgcolor={theme.palette.colors.green[500]}>
                <StyledImg alt='' src={buildingBlockIcon} />
              </CircleIcon>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                }}
              >
                <Typography variant='font14Bold'>
                  {workflow.workflow_name}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.5,
                  }}
                >
                  <Badge
                    label={t('restorationContent.buildingBlock')}
                    variant='macro'
                  />
                  {cannotRestore && workflow.can_restore === false && (
                    <Badge
                      label={t('restorationContent.cannotRestore')}
                      variant='error'
                    />
                  )}
                </Box>
              </Box>
            </Box>
          </ResourceCard>
        ))}

        {deleted_event_configs.map(config => (
          <ResourceCard key={config.event_configuration_id}>
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                gap: 1,
              }}
            >
              <CircleIcon bgcolor='#079DB7'>
                <ReactSVG
                  src={eventTriggerIcon}
                  style={{ height: '20px', width: '20px' }}
                />
              </CircleIcon>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                }}
              >
                <Typography variant='font14Bold'>{config.name}</Typography>
                <Badge
                  label={t('restorationContent.eventConfiguration')}
                  variant='macro'
                />
              </Box>
            </Box>
          </ResourceCard>
        ))}
      </Box>
    </Box>
  );
};

interface RestorationLoadingProps {
  message?: string;
}

export const RestorationLoading = ({
  message = 'Checking restoration requirements...',
}: RestorationLoadingProps) => {
  return (
    <Box
      sx={{
        alignItems: 'center',
        display: 'flex',
        gap: 2,
        justifyContent: 'center',
        minHeight: '80px',
      }}
    >
      <CircularProgress size={20} />
      <Typography variant='font14'>{message}</Typography>
    </Box>
  );
};
